import {Grid, Card, Typography, CardContent, Divider} from "@mui/material";
import React, {useMemo} from "react";
import moment from "moment";
import {TerminalRelease} from "~/apps/packages/warehouse-closure/queries";
import {TerminalReleaseStatusBadge} from "~/apps/packages/warehouse-closure/components/TerminalReleaseStatusBadge";
import {TerminalReleaseCheckResult} from "~/apps/packages/warehouse-closure/components/TerminalReleaseDetail/TerminalReleaseCheckResult";
import {compareByField, formatDate} from "~/apps/core/utils";
import {TypographyWithLabel} from "~/apps/core/ui/TypographyWithLabel";
import {Tag} from "~/apps/core/ui/Tag";


export type TerminalReleaseDetailProps = {
    terminalRelease: TerminalRelease
}


export const TerminalReleaseDetail = (props: TerminalReleaseDetailProps) => {
    const {terminalRelease} = props;
    const chips: { name: string, checked: boolean }[] = [
        {name: "CFS", checked: terminalRelease.IsCFS},
        {name: "Таможенный контроль", checked: terminalRelease.IsCustomControl},
        {name: "Склад временного хранения", checked: terminalRelease.IsDirectToTSW},
    ];
    const hasFailChecks = useMemo(() => {
        return terminalRelease?.CheckList && terminalRelease.CheckList.find(x => !x.CheckResult);
    }, [terminalRelease]);

    return (
        <Grid container direction="column" wrap="nowrap" rowSpacing="16px">
            <Grid item>
                <Card>
                    <CardContent>
                        <Grid container direction="column" wrap="nowrap" spacing="8px">
                            <Grid item container justifyContent="space-between" wrap="nowrap">
                                <TerminalReleaseStatusBadge>
                                    {terminalRelease.State}
                                </TerminalReleaseStatusBadge>
                                <Typography variant="cardText">
                                    {formatDate(moment(terminalRelease.Date, "YYYY-MM-DD"))}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Divider/>
                            </Grid>
                            <Grid item>
                                <Typography variant="cardTitle1" color="primary">
                                    {terminalRelease.Cargo}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Номер заявки">
                                    №{terminalRelease.Number}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Дата ком. разрешения">
                                    {moment(terminalRelease.CommercialReleaseDate, "YYYY-MM-DD").format("DD.MM.YYYY")}
                                </TypographyWithLabel>
                            </Grid>
                            {terminalRelease.Contract?.Number && <Grid item>
                                <TypographyWithLabel label="Договор">
                                    {terminalRelease.Contract.Number}
                                </TypographyWithLabel>
                            </Grid>}
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            {hasFailChecks && terminalRelease.PortResponsible && (
                <Grid item>
                    <Card>
                        <CardContent>
                            <Grid container direction="column" spacing="8px">
                                <Grid item>
                                    <TypographyWithLabel label="Отклонил" noWrap={false}>
                                        {terminalRelease.PortResponsible}
                                    </TypographyWithLabel>
                                </Grid>
                                <Grid item>
                                    <TypographyWithLabel label="Причина отклонения" noWrap={false}>
                                        {terminalRelease.PortComment}
                                    </TypographyWithLabel>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            )}
            <Grid item>
                <Typography variant="screenText" color="primary">
                    Данные груза
                </Typography>
            </Grid>
            <Grid item>
                <Card>
                    <CardContent>
                        <Grid container direction="column" spacing="8px">
                            <Grid item container spacing="8px">
                                {chips.filter(chip => chip.checked).sort(compareByField("name")).map((chip, index) => (
                                    <Grid item key={index}>
                                        <Tag>
                                            {chip.name}
                                        </Tag>
                                    </Grid>
                                ))}
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Документ поступления" noWrap={false}>
                                    {terminalRelease.ArrivalDocument}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Фронт прибытия" noWrap={false}>
                                    {terminalRelease.IncomingFront || <>&mdash;</>} {terminalRelease.IsImport && "(импорт)"}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Фронт отгрузки" noWrap={false}>
                                    {terminalRelease.OutgoingFront || <>&mdash;</>} {terminalRelease.IsExport && "(экспорт)"}
                                </TypographyWithLabel>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            {terminalRelease.CheckList?.length > 0 && (
                <>
                    <Grid item>
                        <Typography variant="screenText" color="primary">
                            Проверки
                        </Typography>
                    </Grid>
                    <Grid item container direction="column" rowSpacing="8px">
                        {terminalRelease.CheckList.map(check => (
                            <Grid item key={check.CheckName}>
                                <TerminalReleaseCheckResult
                                    name={check.CheckName}
                                    success={check.CheckResult}
                                    comment={check.CheckComment}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
        </Grid>
    );
}